import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useMlsMe, useMlsOffered } from "../mlsAccess/useMlsOffered";
import { EXTENSION_CAPTURE_ENABLED, METHOD_PAGE_ENABLED } from "../flags";
import {
  themeVar as colors,
  logoHeight,
  readThemeChoice,
  setThemeChoice,
  type ThemeChoice,
} from "../design-tokens";
import { pageMeasure, type ContentWidth } from "./measures";
import { getMe, logout } from "../cma/api";
import { useModuleStatus } from "../lib/useModuleStatus";
import { NewBriefChooser } from "../home/NewBriefChooser";
import { clearAllDrafts } from "../cma/draftStore";
import { BrandMark } from "./BrandMark";
import { UsagePill } from "./UsageWidgets";

/**
 * UI-001 — the app shell.
 *
 * What it replaces: three hand-rolled copies of the same bare `<main>` + unlinked `<img>` block
 * (IntakeScreen, CmaAmbFlow's review step, NarrativeReportScreen — ResultScreen was a fourth), a
 * 3-step state machine that rendered no evidence of itself, and a product where the browser Back
 * button was the only exit — which on the review step silently destroyed the whole session.
 *
 * It implements `docs/design/NAVIGATION.md` rule 1 ("one persistent top bar, everywhere; logo
 * always = home"), rule 5 ("the wizard always shows where you are"), and rule 6 ("no dead-ends").
 *
 * Deliberately NOT here: the full top-bar destination set from NAVIGATION rule 1 (Analyze ·
 * Reports · Import data · Connections). `/next` has exactly two product routes today; rendering
 * four links to screens that do not exist would be its own dead-end. The bar grows as the routes do.
 */

/**
 * THE WIZARD IS FOUR STEPS, NOT TWO (2026-08-01, owner directive).
 *
 * It used to be "Upload · Review · Report", where Review was one screen carrying the subject grid,
 * the condition control, the whole comparable table, the competing listings, the map, the reading
 * notes and the generate gate — a single scroll long enough that the blocker explaining why
 * Generate was disabled sat well below the table it was talking about.
 *
 * The four steps below are the owner's own sequence. `detail` and `comps` are new; `intake`,
 * `review` and `build` keep their existing names so no caller or test has to be rewritten to say
 * the same thing a different way.
 */
export type ShellStep = "intake" | "detail" | "comps" | "review" | "build";

const STEP_INDEX: Record<ShellStep, number> = { intake: 1, detail: 2, comps: 3, review: 4, build: 5 };
const STEP_LABELS = ["Subject", "Property", "Comps", "Review", "Report"] as const;

/**
 * FIVE NODES, FOUR STEPS — and the two used to disagree in print (fixed 2026-08-10).
 *
 * The rail numbered every node it drew, so the report node was painted "5", while the line above
 * the page said "Step 1 of 4". Both were rendered at once, on the same screen, one under the other:
 * "1 Subject 2 Property 3 Comps 4 Review 5 Report" beneath "STEP 1 OF 4".
 *
 * The four is right and the five was wrong, and this file already said so in prose: the report is
 * the OUTCOME of step 4, not a fifth thing to do — which is also why `build` gets no "Step N of M"
 * line at all, only "Your report". So the count is derived here from the steps that ARE steps, the
 * line interpolates it, and the rail numbers exactly those nodes (see `StepRail`): the report node
 * keeps its place and its label on the rail, as the thing the sequence ends at, but stops claiming
 * to be a fifth step the agent still has to do.
 */
const NUMBERED_STEPS = ["intake", "detail", "comps", "review"] as const satisfies readonly ShellStep[];
const STEP_COUNT = NUMBERED_STEPS.length;

/**
 * Is this a position in the report wizard, or one of the standalone screens that borrows the bar?
 *
 * AN ALLOWLIST, NOT A DENYLIST (2026-08-15). This test used to be written inline as
 * `step !== "extension" && step !== "branding"`, which meant every value added to the `step` union
 * afterwards defaulted to "yes, this is a wizard step" — and then indexed `STEP_INDEX` and
 * `STEP_LINE` with a key neither has. The result is `undefined` flowing into the rail's position
 * and into the "Step N of 4" line above the page, on a screen that is not in the wizard at all.
 *
 * Adding `"crm"` walked straight into it, and the type checker said so, which is the good outcome.
 * Deriving the answer from `STEP_INDEX` itself means the next value added to that union cannot
 * reintroduce it: a step is a wizard step exactly when the step table has a place for it.
 */
function isWizardStep(
  s: ShellStep | "extension" | "branding" | "crm" | "method" | "briefs" | "users" | "mls" | "mlsadmin" | "mlsusage" | "home" | "appraisal",
): s is ShellStep {
  return Object.prototype.hasOwnProperty.call(STEP_INDEX, s);
}

const STEP_LINE: Record<ShellStep, string> = {
  intake: `Step ${STEP_INDEX.intake} of ${STEP_COUNT} · The subject`,
  detail: `Step ${STEP_INDEX.detail} of ${STEP_COUNT} · Property detail and upgrades`,
  comps: `Step ${STEP_INDEX.comps} of ${STEP_COUNT} · The comparable sales`,
  review: `Step ${STEP_INDEX.review} of ${STEP_COUNT} · Review and generate`,
  // The report is the OUTCOME of step 4, not a fifth thing to do — the rail shows it as a node so
  // there is somewhere for the eye to land, but the line above the page never asks the agent to
  // complete a step that consists of reading what they just made.
  build: "Your report",
};

/**
 * THE APP BAR ACROSS WIDTHS — the same fix `marketing/sections.tsx` got an hour earlier, applied to
 * the one bar that is on screen everywhere in the product.
 *
 * THE DEFECT. Every child of this bar was a flex item with no `flex: none`, so once the row ran out
 * of room the items SHRANK below their intrinsic width instead of the row overflowing: labels broke
 * mid-phrase and the email clamped. What did not shrink still ran off the right edge and took the
 * document with it — measured on the live site at a 768px viewport,
 * `document.documentElement.scrollWidth` was 1229 against `innerWidth` 768, with "New analysis",
 * "Branding", "Previous app", the address and "Sign out" all off-screen and the whole page scrolling
 * sideways.
 *
 * THE BREAKPOINTS ARE MEASURED, NOT GUESSED. Rendered at their natural widths in Chromium (headless,
 * 1512px viewport, the signed-in bar, with the address span at its 220px clamp):
 *
 *     logo link 142 · progress rail 551 · account cluster 647
 *     (New analysis 111 + Branding 85 + Previous app 110 + address 220 + Sign out 74 + 4×12 gaps)
 *
 *     ^ MEASURED 2026-08-11, WITH "Previous app" STILL IN THE BAR. That link was removed
 *       2026-08-28, so the cluster is ~122px narrower (110 + one 12px gap) and every breakpoint
 *       below is now conservative by that much: the bar collapses slightly earlier than it needs
 *       to. Left as measured rather than re-derived on paper — these numbers were read off
 *       Chromium and the honest fix is to measure again, not to do arithmetic on a measurement.
 *     header padding 2×24 = 48 · header gaps 2×16 = 32
 *
 *   everything inline, wizard page   142 + 551 + 647 + 32 + 48 = 1420
 *   everything inline, no rail       142 +       647 + 16 + 48 =  853  -> 880
 *   rail + primary + toggle only     142 + 551 + 111 + 42 + 44 + 48 =  938  -> 940
 *   primary + toggle only            142 +       111 + 42 + 28 + 48 =  371
 *
 * so the wizard bar cannot carry the account cluster below 1420px, and cannot carry the rail below
 * 940px. A page with no rail (branding, extension) has a genuinely different item set and collapses
 * at its own measured width rather than borrowing the wizard's — collapsing a bar that still fits is
 * the same class of error as leaving one that doesn't.
 *
 * WHAT MOVES AND WHAT STAYS. Exactly the landing nav's arrangement: the primary action stays in the
 * bar at EVERY width — "New analysis" is what an agent is in this product to click — and everything
 * else moves into one disclosure panel behind a labelled button. Every item is rendered EXACTLY
 * ONCE, in the bar or in the panel, never in both with CSS hiding a copy: a duplicated destination
 * is two tab stops, two screen-reader announcements and two things to keep in sync.
 *
 *   >= 1420        bar: [logo] [rail] [New analysis][Branding][you][Sign out]
 *   940 – 1419     bar: [logo] [rail] [New analysis] [menu]    panel: the account cluster
 *   < 940          bar: [logo] [New analysis] [menu]           panel: the rail, then the cluster
 *
 * The rail goes INTO the panel rather than disappearing below 940 — the landing's other lesson was
 * that hiding real destinations outright ("the section links simply vanished") is worse than putting
 * them one click away, and the rail is the only jump-back-to-a-finished-step control there is. The
 * line above the page ("Step 3 of 4 · The comparable sales") still says where you are at every
 * width with nothing to open, so NAVIGATION rule 5 holds even with the panel shut.
 */
const BAR_INLINE_MIN_WIZARD_PX = 1420;
const BAR_INLINE_MIN_PLAIN_PX = 880;
const BAR_RAIL_INLINE_MIN_PX = 940;
/**
 * Below this the bar drops to its compact metrics — a 44px mark instead of 60px, 16px gutters,
 * tighter gaps. Not a taste call: at full size [logo 142][New analysis 111][menu 42] plus a 48px
 * gutter and 28px of gaps needs 371px, which fits a 390px phone and does NOT fit a 320px one. 520
 * is the same threshold `landing.css` already uses for the same compaction on the same mark, and at
 * the compact metrics the same three items need 309px. */
const BAR_ROOMY_MIN_PX = 520;

/**
 * `matchMedia`, guarded exactly as `marketing/sections.tsx` guards its own: absent (a non-browser
 * render, or jsdom without the setupTests stub) resolves to the WIDE answer, because a bar with
 * everything visible degrades to "crowded" while the collapsed one degrades to "half the app is
 * behind a button that needs JS to open".
 *
 * This drives what is RENDERED, not merely what is painted, which is what keeps every item in the
 * document exactly once.
 */
function useWidthAtLeast(px: number): boolean {
  const query = `(min-width: ${px}px)`;
  const [matches, setMatches] = useState(() =>
    typeof window !== "undefined" && window.matchMedia ? window.matchMedia(query).matches : true,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia(query);
    const onChange = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [query]);
  return matches;
}

/**
 * PAGES THAT ARE NOT WIZARD STEPS (2026-08-01). `/next` grew its first screen that isn't part of
 * the analyze flow — connecting the browser extension — and the shell had no shape for one: `step`
 * was required, so every page had to claim a position in a four-step sequence it wasn't in. A
 * progress rail on a settings page is a lie about where the user is.
 *
 * `"extension"` is accepted as a `step` value and renders NO rail: the bar, the account cluster and
 * the reading measure are shared; the wizard's sense of place is not. `"branding"` (2026-08-10) is
 * the second of these, for exactly the same reason: report branding is a setting, not a step in
 * producing one report, and showing a four-step progress rail above it would claim otherwise.
 *
 * `"crm"` (2026-08-15) is the third, and the least step-like of them: the CRM is a whole module
 * with its own routes, not a page in the report wizard. It is in this union only so the bar can
 * highlight its own link while an agent is inside it. The CRM island renders its own chrome and
 * does not call AppShell — this value exists for any future screen that wants the shared bar with
 * the CRM link marked current.
 *
 * `"method"` (2026-08-15) is the fourth. `/method/:jobId` explains how one finished brief reached
 * its number; it is something you READ ABOUT a completed report, not a step in producing one, so it
 * gets the bar and no rail. Painting the rail there would be the worst version of the lie this
 * union exists to avoid — a four-step progress bar above a page whose whole subject is that the
 * work is already done.
 */
/**
 * THE COLOUR-SCHEME CONTROL  (2026-09-01, W2-UIFIX).
 *
 * The scheme mechanism landed earlier tonight — `design-tokens.ts` reads and writes the stored
 * choice, `main.tsx` applies it at boot, and `app.css` honours `[data-theme]` in both directions —
 * and shipped with nothing a person could click. A dark scheme nobody can reach is a dark scheme
 * that only ever renders by accident, on whichever machines happen to have the OS set that way,
 * which is precisely how the unreadable screens in `w1-harness/*-dark@1920.png` reached a
 * screenshot before they reached anybody's eye.
 *
 * THREE STATES, AND LIGHT IS THE DEFAULT (changed 2026-09-01 on the owner's instruction; the
 * reasoning, including what the change costs, is written out at `design-tokens.ts`'s
 * `DEFAULT_THEME`). "System" remains one of the three options, so a person whose machine is dark
 * gets that back in one click rather than being assumed into it. `readThemeChoice()` returns the
 * default for an unset, unreadable or invalid stored value, so a private window and a browser with
 * storage blocked both land on a real answer rather than on an error.
 *
 * WHY A `<select>` AND NOT A SUN/MOON BUTTON. Three states do not fit a toggle, and the two
 * requirements in the brief settle the rest between them: it must be keyboard-reachable, and its
 * state must not be carried by colour alone. A native select is a first-class tab stop with real
 * arrow-key semantics that no hand-rolled control matches for free, and its state is the WORD
 * showing in the closed control — "System", "Light" or "Dark" — which is text, at every width, in
 * both schemes, and to a screen reader. There is no swatch anywhere in it that a reader has to
 * interpret.
 *
 * IT IS NOT A CLAIM ABOUT ANY FIGURE. Nothing here touches provenance, a source or a number; it
 * changes which palette the same characters are drawn in.
 */
function ThemeChoiceControl({ inPanel }: { inPanel: boolean }) {
  /* Initialised from storage, so the control opens showing what is actually in force rather than
     a default it would then have to correct on the first paint. */
  const [choice, setChoice] = useState<ThemeChoice>(() => readThemeChoice());
  const id = useId();

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        flex: "none",
        padding: inPanel ? "8px 2px 0" : undefined,
      }}
    >
      <label
        htmlFor={id}
        style={{ fontSize: inPanel ? 13 : 12, color: colors.textSecondary, whiteSpace: "nowrap" }}
      >
        Theme
      </label>
      <select
        id={id}
        value={choice}
        onChange={(e) => setChoice(setThemeChoice(e.target.value as ThemeChoice))}
        style={{
          font: "inherit",
          fontSize: 12,
          padding: "4px 6px",
          borderRadius: 6,
          border: `1px solid ${colors.border}`,
          background: colors.bgSurface,
          color: colors.textPrimary,
          cursor: "pointer",
        }}
      >
        {/* "Match system" rather than "System", because the option has to say what it DOES to
            somebody who has never thought about this setting. */}
        <option value="system">Match system</option>
        <option value="light">Light</option>
        <option value="dark">Dark</option>
      </select>
    </span>
  );
}

export function AppShell({
  step,
  content = "prose",
  onBack,
  backLabel = "Back",
  onStepSelect,
  canGoToStep,
  whyStepIsClosed,
  children,
}: {
  step: ShellStep | "extension" | "branding" | "crm" | "method" | "briefs" | "users" | "mls" | "mlsadmin" | "mlsusage" | "home" | "appraisal";
  /** WHAT KIND OF CONTENT THIS PAGE HOLDS — not how many pixels wide it should be (2026-08-11).
   *
   * This used to be `width?: number`, and every screen picked its own pixel count: 880, 1000, 1240,
   * 880, 880. That is how one reading measure ended up applied to a column of prose and to an
   * 18-column comparable-sales grid at the same time, and how a 1512px screen ended up 42% empty
   * margin. `ui/measures.ts` owns the three measures and the fluid rule; a screen says which of the
   * three it is and gets the right one at every viewport. See that file for the derivations. */
  content?: ContentWidth;
  /** Rendered only when supplied — a Back that would destroy state is worse than no Back, so the
   * decision of whether going back is non-destructive stays with the screen that owns the state. */
  onBack?: () => void;
  backLabel?: string;
  /** JUMP BACK TO A COMPLETED STEP (accepted 2026-08-08 — it was being PASSED since 2026-08-07 and
   * silently dropped, because these two props were never declared here. React ignores unknown
   * props and `vite build` strips types without checking them, so the rail rendered, the ticks
   * looked right, and clicking one did nothing.)
   *
   * Both are optional and the rail is read-only without them: an unanswered invalidation prompt
   * deliberately withholds `onStepSelect` so the agent cannot navigate away from the question. */
  onStepSelect?: (target: ShellStep) => void;
  /** Whether a given step may be jumped to. The caller owns this because only it knows what has
   * been reviewed and what leaving would destroy. */
  canGoToStep?: (target: ShellStep) => boolean;
  /** What to say on a node the rail is about to draw inert. See `StepRail`'s own note: the defect
   *  worth fixing was never "the node was inert", it was "the node was inert and silent". */
  whyStepIsClosed?: (target: ShellStep) => string | undefined;
  children: React.ReactNode;
}) {
  const [email, setEmail] = useState<string | null>(null);
  /* ADMIN → PEOPLE (2026-09-04). Read off the SAME `/api/me` call that already ran for the
   * account cluster — one round trip, one source of truth. `=== true` and not truthiness,
   * for the same reason `reports_enabled` below is read that way: an older server with no
   * such key must leave the link HIDDEN. A link that is visible and then 403s is exactly the
   * dead end this bar exists to remove.
   *
   * `is_admin` from the server is the EFFECTIVE privilege — designated AND this session
   * cleared a second factor (SEC-01) — so an admin whose session could not prove itself sees
   * no People link, and the route would refuse them anyway. The link is convenience; the
   * server's `require_admin` is the control, on every request. */
  const [isAdmin, setIsAdmin] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  /* MLS ACCESS (slice mls-access-ui; Release 1.1 Slice A). The links appear exactly when
   * `/api/mls-access/*` will serve, i.e. the server flag is on. Asked once per app load (see
   * useMlsOffered); a 404 reads as "not offered". The NTREIS group and usage links need the caller
   * to be an NTREIS group MANAGER (from their own `/me`), not a platform admin; the server's
   * manager check remains the control on every request. */
  const mlsOffered = useMlsOffered();
  const mlsMe = useMlsMe();
  const mlsManager = mlsMe?.manager === true;

  useEffect(() => {
    let cancelled = false;
    getMe()
      .then((r) => {
        if (cancelled) return;
        setEmail(typeof r.user?.email === "string" ? r.user.email : null);
        setIsAdmin((r.user as { is_admin?: unknown } | undefined)?.is_admin === true);
      })
      .catch(() => {
        /* Non-fatal: the bar renders without an account cluster rather than blocking the app. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* THE CRM LINK IS SERVER-GATED, NOT BUILD-GATED (2026-08-15).
   *
   * `VALUBRIEF_CRM_NEXT_ENABLED` is read per request by the API, so a build-time constant would be
   * a second source of truth that goes stale the moment the flag is flipped on the deploy without
   * a rebuild. `GET /api/next/crm/status` is the one route in that module which answers whether the
   * module exists — every OTHER crm route 404s when the flag is off, and a 404 cannot be told apart
   * from a stale build or a typo'd path.
   *
   * Any failure means the link stays hidden: not signed in, API down, flag off, all the same
   * outcome, because in every one of them the link would lead somewhere the agent cannot use. The
   * bar's own rule is that it grows only as the reachable routes do. */
  /* TWO LINKS, ONE PROBE (2026-08-23). `/status` now answers `enabled` AND `reports_enabled`, and
   * they are INDEPENDENT flags. The shelf (`/briefs`) ships on while `VALUBRIEF_CRM_NEXT_ENABLED`
   * stays off pending the cutover ADR-0004 leaves unscheduled, so the common deployment is
   * shelf-on / CRM-off — a bar that read only `enabled` would hide the one link that works.
   *
   * Both default to FALSE on any failure, and both read `=== true` rather than truthiness: an older
   * server that has no `reports_enabled` key must leave the link hidden, because a link to routes
   * that 404 is exactly the dead end this bar exists to remove. */
  /* THE PROBE MOVED OUT (S03, 2026-09-22), BEHAVIOUR UNCHANGED. It was written inline here, and
     the app home at `/app` has to make the same decision about the same two routes. A second copy
     of a rule whose whole value is three small details — `=== true` not truthiness, false on every
     failure, two independent flags — is a second place for one of them to be got wrong. See
     `lib/useModuleStatus.ts`; the request, the parsing and the failure behaviour are that file's,
     moved verbatim. */
  const { crmEnabled, briefsEnabled } = useModuleStatus();

  async function handleSignOut() {
    setSigningOut(true);
    try {
      await logout();
    } catch {
      /* A failed logout call still ends the client session below — never trap the user in the app. */
    }
    // AND THE DRAFTS, WHICH A HARD NAVIGATION DOES NOT TOUCH (2026-09-07). Neither store is
    // cleared by loading a new page, and since the draft became durable one of the two now
    // outlives the browser session entirely. It carries `preparedFor` — a homeowner's name — so
    // signing out has to take it: the alternative is somebody else's client's name sitting on a
    // shared workstation after the agent has left it.
    clearAllDrafts();
    // A hard navigation, not a router push: it drops every scrap of in-memory job state with the
    // page, which is the point of signing out.
    if (typeof window !== "undefined") window.location.assign("/");
  }

  const inWizard = isWizardStep(step);
  const current = inWizard ? STEP_INDEX[step] : 0;

  // What the bar can carry at this width. See the breakpoint block above for the measurements.
  const actionsInline = useWidthAtLeast(inWizard ? BAR_INLINE_MIN_WIZARD_PX : BAR_INLINE_MIN_PLAIN_PX);
  const railInline = useWidthAtLeast(BAR_RAIL_INLINE_MIN_PX);
  const roomy = useWidthAtLeast(BAR_ROOMY_MIN_PX);
  /* THE BANNER IS BIGGER ON A REAL DESKTOP (2026-08-28, owner: "at least 50% bigger for the
     desktop site … probably good size for mobile").
     Three tiers, not two. `roomy` is 520px — a phone in landscape, not a desktop — so growing the
     mark on that threshold would have made it 50% bigger on the device the owner said was already
     right. `BAR_RAIL_INLINE_MIN_PX` (940) is this file's own MEASURED point at which the bar can
     carry the rail inline, i.e. where the layout is genuinely a desktop one, so the mark grows
     there and nowhere else:
         < 520     logo 44, bar 56   (unchanged)
         520–939   logo 60, bar 56   (unchanged)
         >= 940    logo 90, bar 98   (new) */
  const wideMark = railInline;
  /* ONE SOURCE FOR THE MARK'S HEIGHT, AND THE BAR DERIVED FROM IT. The bar was a flat 56 while the
     mark rendered at 60, so above 520px the logo overflowed its own bar by four pixels, bleeding
     over the bottom border. The logo SIZES the owner signed off (44 on mobile, 60 on tablet) are
     unchanged — only the container grows enough to hold them, which is why `56` survives as a
     floor rather than as a constant. */
  const markPx = wideMark ? logoHeight.appBarWide : roomy ? logoHeight.appBar : logoHeight.appBarCompact;
  /* THE BAR NO LONGER SHRINK-WRAPS THE MARK (2026-08-30). `markPx + 8` meant every bump to the
     logo token inflated the whole page's chrome — which is how a 180px mark produced a 188px bar
     holding 19% of the viewport. A bar is a bar: 60px on a desktop, 56 on a tablet, 52 on a phone,
     and the mark sits inside it. The prototype's is 54. */
  const barPx = wideMark ? 54 : roomy ? 54 : 52;
  const railInBar = inWizard && railInline;
  const railInPanel = inWizard && !railInline;
  const collapsed = !actionsInline || railInPanel;

  const [open, setOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const menuId = useId();
  const headerRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const closeMenu = useCallback(() => setOpen(false), []);

  // Widening past the breakpoint closes the panel. Without this, opening at 900px and then widening
  // leaves `open` true, so re-narrowing later reveals a panel nobody asked for a second time.
  useEffect(() => {
    if (!collapsed) setOpen(false);
  }, [collapsed]);

  // A disclosure, not a modal: no focus trap and no scroll lock — it is a short list anchored under
  // the bar, and trapping focus would be a heavier promise than this UI keeps.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      // Escape without this leaves focus on a node that just stopped rendering; the browser drops
      // it to <body> and the next Tab restarts from the top of the document.
      toggleRef.current?.focus();
    };
    const onPointer = (e: PointerEvent) => {
      if (!headerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onPointer);
    };
  }, [open]);

  /** A bar link's chrome. `inPanel` swaps the pill for a full-width row: the panel is a stacked
   *  list, and a row of pills in a column reads as a pile of buttons.
   *
   *  DESTINATIONS ARE TEXT; THE ACTION IS A BUTTON (2026-08-30, owner: "make it look like your
   *  demos"). Every item in this bar used to render as the same bordered pill on a white ground —
   *  "New analysis", "CRM", "Branding" and "Sign out" all identical — so the bar read as a row of
   *  four equally-weighted boxes and the one thing an agent comes here to do had no more weight
   *  than a settings link. Three levels now, and they are a HIERARCHY rather than four coats of
   *  paint:
   *
   *    primary       filled accent. Exactly one, and this file already had the rule that it
   *                  survives every viewport — it now looks like it.
   *    destination   plain text. A link that goes somewhere needs a label, not a box.
   *    current       the accent-soft pill, kept from the old chrome and now the ONLY thing in the
   *                  bar wearing one, so it reads as "you are here" instead of as decoration.
   *
   *  The two rules that fixed the 2026-08-11 overflow are unchanged and load-bearing: `flex: none`
   *  and `whiteSpace: nowrap`, so a bar out of room overflows visibly rather than hyphenating the
   *  product's own labels. The measured breakpoints above assumed the pill's 2×12px padding plus a
   *  1px border; text links are NARROWER than that, so every breakpoint stays conservative — the
   *  bar collapses slightly earlier than it now needs to, which is the safe direction. Re-measuring
   *  is the honest fix and is a separate change from restyling. */
  function linkStyle(active: boolean, inPanel: boolean, muted = false): React.CSSProperties {
    const color = active ? colors.accent : muted ? colors.textSecondary : colors.textPrimary;
    return inPanel
      ? {
          display: "block",
          padding: "11px 2px",
          fontSize: 15,
          color,
          textDecoration: "none",
          borderBottom: `1px solid ${colors.border}`,
        }
      : {
          /* IN THE BAR THE PROTOTYPE'S `.nav` OWNS THE CHROME (2026-08-30). Its rule is
             `.nav {display:flex;gap:18px;font-size:13px;color:var(--muted)}` with
             `.nav b {color:var(--text);font-weight:600}` for the current page — plain text, one
             gap, no boxes. All this leaves inline is the two properties that stopped the
             2026-08-11 overflow, which are behavioural rather than decorative. */
          flex: "none",
          whiteSpace: "nowrap",
          textDecoration: "none",
        };
  }


  /* Built once and placed EITHER in the bar OR in the panel — never both. `inPanel` only changes
     the chrome; which items exist is decided by auth, exactly as it was before.

     SPLIT IN TWO (2026-08-30) so the bar can read left to right as destinations → action →
     account. It used to be one fragment rendered AFTER "New analysis", which put the primary
     action to the LEFT of three destinations and immediately beside the email address — the
     button an agent clicks most sitting in the middle of the settings cluster. Same items, same
     auth gates, same render-exactly-once rule; only the grouping changed. */
  const destinationLinks = (inPanel: boolean) => (
    <>
      {/* HOME (S03, 2026-09-22) — FIRST, AND UNGATED.

          THE DEFECT: there was no app home to link to. The wordmark to the left of this row is
          labelled "ValuBrief — home" and points at `/`, which was the MARKETING page — so the only
          control in the product's chrome that said "home" took a signed-in agent out of the
          product. `/app` is that missing start, and it is one click from every screen the bar is
          on.

          No gate, because the route has none beyond `RequireAuth` — and this bar only ever renders
          inside a screen that already passed it. A plain <a> like every other link here: leaving a
          job should drop its in-memory state. `/` now sends a signed-in visitor to `/app` as well,
          so the wordmark and this link finally agree about where home is. */}
      <a href="/app" onClick={closeMenu} aria-current={step === "home" ? "page" : undefined} style={linkStyle(step === "home", inPanel)}>
        Home
      </a>
      {/* NAVIGATION rule 1's destination set, grown by exactly one — the screen that now
          exists. Rendering a link to a route that doesn't exist is the dead-end this bar was
          built to remove, so the bar still grows only as the routes do. A plain <a> for the
          same reason "New analysis" is one: leaving a job should drop its in-memory state. */}
      {/* WITHDRAWN 2026-08-07 with the extension itself (`src/flags.ts`). The rule above is
          exactly why this is gated rather than left alone: with the route redirecting, this
          link would be the dead end this bar was built to remove. */}
      {EXTENSION_CAPTURE_ENABLED && (
        <a href="/extension" onClick={closeMenu} aria-current={step === "extension" ? "page" : undefined} style={linkStyle(step === "extension", inPanel)}>
          Extension
        </a>
      )}
      {/* BRANDING (2026-08-10). A settings link, not a wizard step, so it sits in the account
          cluster rather than on the rail. It has to be reachable from inside the app for the
          same reason the Extension link did: the only other way to change branding is a form in
          the legacy app, and after the apex swap that form is on a hostname nobody visits while
          every brief keeps rendering from the row it last saved. Highlighted when active, the
          same treatment the Extension link gets. */}
      {/* YOUR BRIEFS (2026-08-23). The route back to work already done.

          FIRST in this cluster, and it is a destination rather than a setting: every brief has
          always lived at a stable URL and nothing listed it, so an agent who closed the tab had no
          way back to their own work at any layer. That is the gap this link closes, which makes it
          the most-returned-to link in the bar.

          It follows the same pattern as its neighbours rather than sitting beside "New analysis" in
          the bar at every width. The bar's promise is that ONE primary action survives every
          viewport; a second one competing with it at 320px would cost that promise more than this
          link gains. It is one tap away inside the menu when the bar collapses.

          Server-gated on `reports_enabled`, which is a DIFFERENT flag from the CRM's below — see
          the probe above. */}
      {briefsEnabled && (
        <a href="/briefs" onClick={closeMenu} aria-current={step === "briefs" ? "page" : undefined} style={linkStyle(step === "briefs", inPanel)}>
          Your briefs
        </a>
      )}
      {/* THE CRM (2026-08-15). Shown only when the server says the module is on — see the
          `crmEnabled` effect above for why that answer cannot come from a build constant. Placed
          before Branding because it is a destination an agent works in, not a setting they visit
          once. Same plain <a> as its neighbours: leaving a job should drop its in-memory state. */}
      {crmEnabled && (
        <a href="/crm" onClick={closeMenu} aria-current={step === "crm" ? "page" : undefined} style={linkStyle(step === "crm", inPanel)}>
          CRM
        </a>
      )}
      {/* THE METHOD PAGE (2026-08-15). Gated on the build constant, not on a `/status` probe like
          the CRM above: `/method` adds no endpoint and no server capability to discover — it reads
          the same result call the report screen already makes — so there is nothing for the server
          to be asked. See `flags.ts` for that distinction written out, and for why the constant is
          `false` today.

          The bar's rule is that it grows only as the reachable routes do, and this link honours it
          in both directions: the route exists when the flag is on, and the bare `/method` it points
          at is a real page (it explains what the page is for and offers a way to open a brief)
          rather than the blank 200 an unmatched path renders in this SPA. */}
      {METHOD_PAGE_ENABLED && (
        <a href="/method" onClick={closeMenu} aria-current={step === "method" ? "page" : undefined} style={linkStyle(step === "method", inPanel)}>
          Method
        </a>
      )}
      <a href="/branding" onClick={closeMenu} aria-current={step === "branding" ? "page" : undefined} style={linkStyle(step === "branding", inPanel)}>
        Branding
      </a>
      {/* USERS (2026-09-04). The only link in this bar gated on WHO is signed in rather than on
          what the deployment has switched on — and it follows the bar's own rule either way: it
          appears exactly when the route behind it will serve. */}
      {mlsOffered && (
        <a href="/mls-access" onClick={closeMenu} aria-current={step === "mls" ? "page" : undefined} style={linkStyle(step === "mls", inPanel)}>
          MLS access
        </a>
      )}
      {mlsOffered && mlsManager && (
        <a href="/mls-access/admin" onClick={closeMenu} aria-current={step === "mlsadmin" ? "page" : undefined} style={linkStyle(step === "mlsadmin", inPanel)}>
          NTREIS group
        </a>
      )}
      {mlsOffered && mlsManager && (
        <a href="/mls-access/usage" onClick={closeMenu} aria-current={step === "mlsusage" ? "page" : undefined} style={linkStyle(step === "mlsusage", inPanel)}>
          NTREIS usage
        </a>
      )}
      {isAdmin && (
        <a href="/users" onClick={closeMenu} aria-current={step === "users" ? "page" : undefined} style={linkStyle(step === "users", inPanel)}>
          Users
        </a>
      )}
    </>
  );

  const accountCluster = (inPanel: boolean) => (
    <>
      {/* THE "PREVIOUS APP" LINK IS GONE (2026-08-28, owner: "the previous app somehow got
          destroyed and sucks — just remove the link").

          It shipped 2026-08-10 as a bridge to the ~15 routers never ported (the workspace, the
          Narrative Lab, delivery templates, batch), on the reasoning that after the apex swap the
          only way to reach them was to remember a hostname. A bridge to something broken is worse
          than no bridge: it is a link, in the product's own chrome, to a worse version of itself.

          `legacyAppUrl()` STAYS. It has three other call sites and they are a different thing —
          the "Request access" CTAs on the landing and the login screen's "No account yet?" line,
          which are the signup path while this client has no /signup route of its own. Deleting
          the helper along with this link would have silently removed the primary conversion path,
          which is the exact failure mode `lib/legacyApp.ts` was written to prevent. Those three
          are the owner's separate call. */}
      {email && (
        <span
          style={{
            fontSize: inPanel ? 13 : 12,
            color: colors.textSecondary,
            maxWidth: inPanel ? undefined : 220,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: "none",
            padding: inPanel ? "12px 2px 4px" : undefined,
          }}
        >
          {email}
        </span>
      )}
      {/* Beside the account controls in both arrangements: it is a preference about this browser,
          which is the same kind of thing as the address and the sign-out beside it. */}
      <ThemeChoiceControl inPanel={inPanel} />
      <button
        type="button"
        onClick={handleSignOut}
        disabled={signingOut}
        className={inPanel ? undefined : "btn"}
        style={
          inPanel
            ? {
                padding: "10px 12px",
                marginTop: 8,
                borderRadius: 8,
                border: `1px solid ${colors.border}`,
                background: colors.bgSurface,
                color: colors.textPrimary,
                fontSize: 13,
                cursor: signingOut ? "not-allowed" : "pointer",
                opacity: signingOut ? 0.55 : 1,
              }
            : { flex: "none", whiteSpace: "nowrap", opacity: signingOut ? 0.55 : 1 }
        }
      >
        {signingOut ? "Signing out…" : "Sign out"}
      </button>
    </>
  );

  const toggleBar: React.CSSProperties = {
    display: "block",
    width: 16,
    height: 2,
    borderRadius: 2,
    background: colors.textPrimary,
  };

  return (
    /* `vb-ui` is the scope the ported prototype stylesheet lives under — see mock-design.css.
       Applied once, here, so every screen inside the shell gets the prototype's tokens, its
       14px/1.45 base, its box-sizing reset and its hover/transition rules. The marketing pages do
       not render through AppShell and are deliberately untouched. */
    <div className="vb-ui" style={{ minHeight: "100vh" }}>
      {/* MIN-HEIGHT BELOW 520px (2026-10-28, clean-pass audit #1). At 390px the bar's rows wrap (mark,
            usage pill, New brief, menu button); a fixed height left the wrapped row hanging below
            the bar, on top of the first words of every signed-in page. Below `BAR_ROOMY_MIN_PX` the
            bar therefore grows with its rows. From 520px up it keeps the exact fixed height it had,
            so tablet and desktop layouts do not move by a pixel. */}
      <header ref={headerRef} className="bar" style={roomy ? { height: barPx } : { minHeight: barPx }}>
          <a href="/" aria-label="ValuBrief — home" style={{ display: "flex", alignItems: "center", flex: "none" }}>
            <BrandMark height={markPx} />
          </a>

          {/* THE DESTINATIONS SIT BESIDE THE MARK, NOT AGAINST THE RIGHT EDGE (2026-08-30).
              Both approved prototypes put `.nav` immediately after the logo and reserve the right
              of the bar for ACTIONS — `Credits · Back · Continue` in the layout file, `Search ·
              New engagement · Start a brief` in the CRM one. Rendering the same links inside
              `.barRight` pushed every control against the right edge and left a long empty span
              after the mark, which measured as the second-largest deviation from the prototype
              after the logo itself. Same links, same component, same collapse rule — only the
              side changes. */}
          {!collapsed && <nav className="nav">{destinationLinks(false)}</nav>}

          <div className="barRight">
            {/* THE MONTHLY REPORT LIMIT PILL (§6, board 1 option A). Rendered on every signed-in
                screen, at every width — unlike the rail and the account cluster it does not move
                into the collapsed panel, because the design calls for it to be visible everywhere,
                the same way "New brief" stays in the bar at every width. It renders nothing for an
                admin (`limit: null`) and nothing before the first round trip answers, so it costs
                nothing on a deployment or a session this does not apply to.

                WIDTH IS NOT RE-MEASURED HERE. The breakpoints above (`BAR_INLINE_MIN_WIZARD_PX`
                etc.) were measured in Chromium before this pill existed; adding an item changes
                the bar's required width and those constants are now conservative rather than
                exact. Re-measuring is the honest fix — flagged for the Playwright pass (§6, L5)
                that screenshots this bar next, rather than guessed at here. */}
            <UsagePill />

            {/* IN-APP NAVIGATION (2026-08-01, owner report: "there is no way to get back to the main
                screen or navigate the site"). Once inside /cma/:jobId the only controls were the logo
                — which leaves for the marketing page — and Sign out. There was no way to start a
                second analysis without editing the URL.

                A plain <a>, not a router push, and to /cma with no job id: starting fresh must drop
                every scrap of the previous job's in-memory state, which is exactly what a hard
                navigation guarantees and a client-side route change does not.

                THE PRIMARY ACTION STAYS IN THE BAR AT EVERY WIDTH. Putting the one thing an agent is
                here to click behind a menu button is the failure mode hamburgers are rightly
                criticised for. */}
            {/* THE PRIMARY ACTION STAYS IN THE BAR AT EVERY WIDTH — putting the one thing an agent
                is here to click behind a menu button is the failure mode hamburgers are rightly
                criticised for. `btn p` is the prototype's primary; the geometry, the hover and the
                press all come from its stylesheet.

                IT WAS A PLAIN `<a href="/cma">` UNTIL 2026-09-22, and the reason given was that
                starting fresh must drop every scrap of the previous job's in-memory state, which a
                hard navigation guarantees and a client-side route change does not. THAT REASON
                STILL HOLDS AND IS STILL HONOURED — it just moved one step later. This opens the
                same `NewBriefChooser` that `/app` and `/briefs` open, and every card in that
                chooser is itself a plain `<a href="/cma">` (`NewBriefChooser.tsx:188`), so the hard
                navigation is unchanged. What changed is that the bar no longer skips the chooser:
                the owner's rule is one primary action with one behaviour, and a bar that jumped
                straight into the builder while the page beside it asked which vertical you meant
                was two answers to one question. */}
            {/* SECONDARY, NOT A SECOND PRIMARY (owner decision, 2026-09-23; built overnight, PENDING
                APPROVAL). "One PRIMARY action per screen, in the page's main content. A header entry
                is navigation, not a second primary: it gets secondary styling or it goes." `/app`,
                `/briefs` and `/appraisal` each carry their own `btn p` "New brief" in the page; this
                bar entry stays — it is the only way to a new brief from the builder, the CRM and the
                editor — as the prototype's plain `btn`, a hairline button, same chooser behind it. */}
            <button type="button" className="btn" data-testid="shell-new-brief"
                    onClick={() => setChooserOpen(true)}>
              New brief
            </button>

            {!collapsed && accountCluster(false)}

            {collapsed && (
              <button
                type="button"
                ref={toggleRef}
                aria-expanded={open}
                aria-controls={menuId}
                aria-label={open ? "Close menu" : "Open menu"}
                onClick={() => setOpen((v) => !v)}
                style={{
                  display: "inline-flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 4,
                  width: 42,
                  height: 36,
                  padding: 0,
                  border: `1px solid ${colors.border}`,
                  borderRadius: 8,
                  background: colors.bgSurface,
                  cursor: "pointer",
                  flex: "none",
                }}
              >
                {/* Three bars as three spans, not an icon font and not a pseudo-element: this file
                    styles inline, and inline styles cannot express ::before/::after. */}
                <span aria-hidden="true" style={toggleBar} />
                <span aria-hidden="true" style={toggleBar} />
                <span aria-hidden="true" style={toggleBar} />
              </button>
            )}
          </div>

        {collapsed && (
          <div
            id={menuId}
            hidden={!open}
            style={{
              // `hidden` alone would lose to this inline `display`, so the two are driven from the
              // same boolean: the attribute keeps the contents out of the accessibility tree, the
              // display keeps them off the screen.
              display: open ? "flex" : "none",
              flexDirection: "column",
              alignItems: "stretch",
              gap: 4,
              padding: roomy ? "10px 24px 16px" : "10px 16px 16px",
              borderTop: `1px solid ${colors.border}`,
              /* WAS `rgba(255,255,255,.98)` — a literal white that stayed white on a dark page,
                 which is the same class of defect `themeVar` exists to close. The surface token
                 resolves per scheme; the opacity is dropped rather than guessed at, because a
                 translucent panel over a dark page is a different design decision and not one to
                 make in passing. */
              background: colors.bgSurface,
            }}
          >
            {railInPanel && (
              <StepRail
                current={current}
                onSelect={onStepSelect ? (target) => { closeMenu(); onStepSelect(target); } : undefined}
                canGo={canGoToStep}
                whyNot={whyStepIsClosed}
                vertical
              />
            )}
            {!actionsInline && (
              <>
                {destinationLinks(true)}
                {accountCluster(true)}
              </>
            )}
          </div>
        )}
      </header>

      {/* THE STEP RAIL IS ITS OWN ROW, AS IT IS IN THE PROTOTYPE (2026-08-30).
          It lived inside `.bar`, sharing one 60px row with the logo, four destination links, the
          primary action and the account cluster. The approved prototype gives it a band of its own
          directly under the bar — `.steps`, white, one hairline, 48px — and that second row is most
          of why the prototype's header reads as organised and ours read as crowded. Comparing the
          two at 1512 with computed styles, `.steps` and `.sd` were simply MISSING from this app:
          the port shipped their CSS and nothing ever rendered the element.
          `railInBar` keeps its meaning — the rail is inline at this width rather than in the
          collapsed menu — it just no longer means "inline INSIDE the bar". */}
      {/* ONE CHOOSER PER SHELL, not one per surface. `/app` and `/briefs` each mount their own
          because each owns its own primary action; the bar's is the shell's, so it lives here and
          is available at every width, including inside the collapsed menu. */}
      <NewBriefChooser open={chooserOpen} onClose={() => setChooserOpen(false)} />

      {railInBar && (
        <div className="steps">
          <StepRail current={current} onSelect={onStepSelect} canGo={canGoToStep}
                    whyNot={whyStepIsClosed} />
        </div>
      )}

      {/* The horizontal measure comes from `pageMeasure` and the gutter is part of it, which is why
          the padding here is vertical only: the empty space at the edges is one number that scales
          with the viewport, not a 24px padding sitting inside a 276px margin.

          `bleed` TAKES NO PADDING AT ALL (2026-08-30). It is the kind whose own content supplies
          every edge — the CRM's `.frame` runs its rail to the top of the canvas and its `.chead`
          applies the vertical rhythm. The shared 28px left a dead strip between the bar and the
          rail, which reads as a rendering fault rather than as spacing. */}
      <main
        style={{
          ...pageMeasure(content),
          padding: content === "bleed" ? 0 : "28px 0 96px",
        }}
      >
        {/* `flexWrap` because this row mixes a control with a sentence: at 320px "← Start over" and
            "STEP 3 OF 4 · THE COMPARABLE SALES" do not share a line, and without the wrap the
            button would be squeezed below its intrinsic width and hyphenate — the same defect the
            bar had. The button keeps its width; the sentence is what reflows. */}
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12, marginBottom: 10 }}>
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              style={{
                padding: "4px 8px",
                marginLeft: -8,
                borderRadius: 6,
                border: "none",
                background: "transparent",
                color: colors.accent,
                fontSize: 13,
                cursor: "pointer",
                flex: "none",
                whiteSpace: "nowrap",
              }}
            >
              ← {backLabel}
            </button>
          )}
          {inWizard && (
            <p style={{ margin: 0, fontSize: 12, color: colors.textSecondary, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              {STEP_LINE[step]}
            </p>
          )}
        </div>
        {children}
      </main>
    </div>
  );
}

/** The rail's node order, index-aligned to STEP_LABELS. Derived from the same `ShellStep` union
 * the rest of the shell uses, so a step added to one cannot go missing from the other. */
const STEP_ORDER: readonly ShellStep[] = ["intake", "detail", "comps", "review", "build"] as const;

/** NAVIGATION rule 5 — "the wizard always shows where you are." Colour never carries the state
 * alone: the current node is also marked `aria-current`, and every node keeps its text label.
 *
 * AND NOW "where you have been" IS ALSO SOMEWHERE YOU CAN GO (2026-08-08). A node is a real
 * `<button>` when the caller says that step is reachable, and an inert `<span>` otherwise — never
 * a disabled button, because a disabled control still reads as a control and invites the click it
 * will refuse. The caller decides reachability; this component never guesses, and with no
 * `onSelect` it behaves exactly as it always did.
 *
 * `vertical` (2026-08-10) is the SAME rail stacked, for the disclosure panel the bar collapses into
 * below 940px — see the breakpoint block at the top of this file. It is a layout switch and nothing
 * else: same nodes, same order, same labels, same buttons, rendered exactly once either way. Five
 * nodes side by side need 551px, which no phone has; stacked they need one column and always fit. */
export function StepRail({
  current,
  onSelect,
  canGo,
  whyNot,
  vertical = false,
}: {
  current: number;
  onSelect?: (target: ShellStep) => void;
  canGo?: (target: ShellStep) => boolean;
  /**
   * WHY A NODE IS NOT A DOOR (2026-09-07). A control that will not respond and does not say why is
   * the single most confusing thing a screen can contain, and this rail was full of them: an agent
   * on step 1 met four nodes that did nothing and offered no account of themselves. The gate is
   * open now, so at most one node is ever inert — but the rule is worth keeping in the component
   * rather than in the caller, because the next inert node somebody adds inherits it.
   */
  whyNot?: (target: ShellStep) => string | undefined;
  vertical?: boolean;
}) {
  return (
    <nav
      aria-label="Progress"
      style={{ display: "flex", alignItems: "center", gap: 8, flex: "none", minWidth: 0 }}
    >
      <ol
        style={{
          display: "flex",
          flexDirection: vertical ? "column" : "row",
          alignItems: vertical ? "stretch" : "center",
          gap: vertical ? 2 : 8,
          listStyle: "none",
          margin: 0,
          padding: 0,
          ...(vertical ? { width: "100%" } : null),
        }}
      >
        {STEP_LABELS.map((label, i) => {
          const n = i + 1;
          const done = n < current;
          const active = n === current;
          // FOUR NUMBERED STEPS, FIVE NODES — see NUMBERED_STEPS above. The report node is the
          // sequence's outcome, so it keeps its place and its label but carries no step number;
          // numbering it "5" is what used to contradict the "Step 1 of 4" line under this rail.
          const numbered = n <= STEP_COUNT;
          const target = STEP_ORDER[i];
          const navigable = Boolean(onSelect && !active && target && canGo?.(target));
          const NodeTag = (navigable ? "button" : "span") as "button" | "span";
          // Only asked about a node that is about to be drawn inert, and never about the one the
          // agent is standing on — "you cannot click this because you are here" is not a reason.
          const closedBecause = !navigable && !active && target ? whyNot?.(target) : undefined;
          return (
            <li
              key={label}
              style={{
                display: "flex",
                alignItems: "center",
                // The prototype's `.sd` carries its own right margin; a gap here would double it.
                gap: vertical ? 8 : 0,
                flex: "none",
                whiteSpace: "nowrap",
              }}
            >
              <NodeTag
                {...(navigable
                  ? {
                      type: "button" as const,
                      onClick: () => onSelect?.(target),
                      // aria-label, not title alone. The node's visible content is the bare step
                      // name ("Property") because the numbered circle beside it is aria-hidden —
                      // so without this the control announces itself as "Property", which says
                      // where you are rather than what the button does. `title` stays for the
                      // sighted hover; the accessible name is what a screen reader reads out and
                      // what the rail's own tests query by.
                      //
                      // AND IT SAYS WHICH WAY IT GOES (2026-09-07). Every node is a door now, so a
                      // rail that greets an agent on step 1 with four controls all saying "Go back
                      // to" is telling them, four times, that the thing they want to do is not on
                      // offer. Behind you is "Go back to"; ahead of you is "Go to". The word is
                      // read off the node's own position, so it cannot drift from the gate.
                      "aria-label": `${n < current ? "Go back to" : "Go to"} ${label}`,
                      title: `${n < current ? "Go back to" : "Go to"} ${label}`,
                    }
                  : {})}
                {...(closedBecause
                  ? {
                      // `title` for the pointer and `aria-label` for the screen reader, which
                      // otherwise hears only the step's name and no account of why nothing happens.
                      title: closedBecause,
                      "aria-label": `${label} — ${closedBecause}`,
                    }
                  : {})}
                aria-current={active ? "step" : undefined}
                /* THE PROTOTYPE'S OWN STEP (2026-08-30). `.sd` / `.sd.done` / `.sd.now` and the
                   `.n` disc inside are shipped in `mock-design.css` and were rendering nowhere:
                   measured, our node was 461×18 at 14px/400 where the prototype's is 88×40 at
                   12.5px/600 with a 20px disc. The DOM was already the right shape — a node with
                   a circle and a label — so this is a class swap, not a rewrite.
                   HORIZONTAL ONLY. The prototype has no vertical step rail; the collapsed panel
                   keeps its own styles, which is why every inline value below is still here. */
                className={vertical ? undefined : `sd${done ? " done" : ""}${active ? " now" : ""}`}
                style={vertical ? {
                  // `font` FIRST, AND THE ORDER IS LOAD-BEARING (fixed 2026-08-15).
                  //
                  // A button does not inherit the page font -- the UA sets its own -- so this
                  // shorthand is genuinely needed to make a navigable node identical to an inert
                  // `<span>`. But it IS a shorthand: it resets family, size, weight and
                  // line-height together, so anything it follows in this object it silently eats.
                  //
                  // It used to sit below `fontSize` and `fontWeight`, and it ate them. Measured in
                  // a real browser: every rail node computed to `font-size: 16px` -- the inherited
                  // body size, not the 12px written two lines above it -- beside an 18px numbered
                  // circle. React reported the other half itself, twice per render: "Updating a
                  // style property during rerender (fontWeight) when a conflicting property is set
                  // (font)", because the active node's 600 was being reapplied after the shorthand
                  // had cleared it. Order-dependent emphasis on the one control that says where you
                  // are in the wizard.
                  //
                  // Declared first, the shorthand does its single job -- drop the UA font -- and
                  // the specific properties below it win. Do not sort these keys alphabetically.
                  font: "inherit",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  fontWeight: active ? 600 : 400,
                  color: active ? colors.accent : done ? colors.verified : colors.textSecondary,
                  // A button carries a UA border, padding and background; strip them so a
                  // navigable node is pixel-identical to an inert one and only the cursor differs.
                  border: "none",
                  background: "none",
                  padding: "8px 0",
                  margin: 0,
                  cursor: navigable ? "pointer" : "default",
                  textDecoration: "none",
                  width: "100%",
                } : undefined}
              >
                <span
                  aria-hidden="true"
                  className={vertical ? undefined : "n"}
                  style={vertical ? {
                    width: 18,
                    height: 18,
                    flex: "none",
                    borderRadius: "50%",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    border: `1px solid ${active ? colors.accent : done ? colors.verified : colors.border}`,
                    background: done ? colors.verified : active ? colors.accentSoft : "transparent",
                    color: done ? "#fff" : active ? colors.accent : colors.textSecondary,
                  } : undefined}
                >
                  {/* The outcome node has no number to show; reached, it is a tick like any other
                      finished node, and unreached it is the empty circle the sequence ends at. */}
                  {done || (!numbered && active) ? "✓" : numbered ? n : ""}
                </span>
                {label}
              </NodeTag>
              {!vertical && n < STEP_LABELS.length && (
                <span aria-hidden="true" style={{ width: 16, height: 1, background: colors.border, display: "inline-block" }} />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
